import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { CompleteAccountModalContext, ModalContext, openPlaceAbsenteeBidModal } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { fetchBidIncrementsIfNeeded } from '@/redux/modules/bidIncrements/bidIncrements.actions';
import { fetchCatalogsIfNeeded } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { fetchItemDetailsIfNeeded } from '@/redux/modules/itemDetail';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryBidderMaxBid,
    getItemSummaryCatalogId,
    getItemSummaryLeadingBid,
    getItemSummaryStartPrice,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getNextBidMinimum } from '@/utils/absenteeBids';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useTrackPlaceBidPressedAnalytics } from '@/utils/amplitude/analytics';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import useIsWiderThan from '@/hooks/useIsWiderThan';

type PlaceBidButtonProps = {
    itemId: number;
};

const PlaceBidButton = ({ itemId }: PlaceBidButtonProps) => {
    const dispatch = useAppDispatch();
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);
    const shouldUseShortBidText = !useIsWiderThan(868);
    const placeBidText = !isMobile && shouldUseShortBidText ? 'bid' : 'place_bid';
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const catalogRegistration = useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const { approved, registered } = catalogRegistration;

    const userHasPlacedBid = !isMobile && bidderHasBid;
    const userHasRegisteredForAuction = (approved || registered) && !bidderHasBid;
    const bidIncrements = useAppSelector((state) => getBidIncrementsByItemId(state, itemId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const newBidMinimum = getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

    const trackPlaceBidPressedAnalytics = useTrackPlaceBidPressedAnalytics(catalogId, itemId);

    const [bidAmount, setBidAmount] = React.useState(
        getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );

    const modalContext = React.useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.placeBid,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.PlaceBid,
        }),
        [itemId]
    );

    const placeBid = async () => {
        trackPlaceBidPressedAnalytics();

        await Promise.all([
            dispatch(fetchItemDetailsIfNeeded([itemId])),
            dispatch(fetchBidIncrementsIfNeeded(catalogId)),
            dispatch(fetchCatalogsIfNeeded({ catalogIds: [catalogId] })),
        ]);

        dispatch(
            openPlaceAbsenteeBidModal({
                bidAmount,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    };

    useEffect(() => {
        setBidAmount(newBidMinimum);
    }, [bidIncrements, newBidMinimum]);

    return (
        <StyledPlaceBidButton
            $haveButtonSpanAcrossCard={userHasRegisteredForAuction || userHasPlacedBid}
            $isPlaceBidText={!bidderHasBid}
            data-testid="place-bid-container"
            onClick={placeBid}
            size="sm"
        >
            <FormattedMessage id={bidderHasBid ? 'increase_bid' : placeBidText} />
        </StyledPlaceBidButton>
    );
};

export default PlaceBidButton;

const StyledPlaceBidButton = styled(Button)<{
    $haveButtonSpanAcrossCard?: boolean;
    $isPlaceBidText?: boolean;
}>`
    width: 63%;
    margin-right: 5px;
    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        width: 122px;
        margin-right: 8px;
    }
    @media (max-width: 375px) {
        width: 123px;
    }

    ${({ $haveButtonSpanAcrossCard, $isPlaceBidText }) =>
        $haveButtonSpanAcrossCard &&
        css`
            width: 100%;
            margin-right: 0;
            @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
                width: 211px;
                margin-right: 0;
            }
            @media (max-width: ${theme.breakpoints.mobileWidth}) {
                width: ${$isPlaceBidText ? '123px' : '100%'};
            }
        `}
`;
